// @ts-check
/**
 * Debug hooks for block dev-tools integration.
 *
 * This module provides:
 * - Debug callback hooks for dev-tools integration (visual overlays, logging, outlet boundaries)
 * - Ghost component creation for visualizing hidden blocks
 * - Debug console grouping utilities
 *
 * The debug hooks use TrackedMap for reactivity, enabling Ember's reactivity system
 * to trigger re-renders when callbacks are set/cleared.
 *
 * @module discourse/lib/blocks/-internals/debug-hooks
 */

import { trackedMap } from "@ember/reactive/collections";
import { FAILURE_TYPE } from "discourse/lib/blocks/-internals/patterns";

/**
 * Callback key constants for the debug hooks registry.
 * Use these instead of magic strings when calling debugHooks.getCallback/setCallback.
 */
export const DEBUG_CALLBACK = Object.freeze({
  BLOCK_DEBUG: "blockDebug",
  BLOCK_LOGGING: "blockLogging",
  VISUAL_OVERLAY: "visualOverlay",
  GHOST_BLOCKS: "ghostBlocks",
  OUTLET_INFO_COMPONENT: "outletInfoComponent",
  CONDITION_LOG: "conditionLog",
  COMBINATOR_LOG: "combinatorLog",
  CONDITION_RESULT: "conditionResult",
  PARAM_GROUP_LOG: "paramGroupLog",
  ROUTE_STATE_LOG: "routeStateLog",
  OPTIONAL_MISSING_LOG: "optionalMissingLog",
  START_GROUP: "startGroup",
  END_GROUP: "endGroup",
  LOGGER_INTERFACE: "loggerInterface",
  GHOST_CHILDREN_CREATOR: "ghostChildrenCreator",
});

/**
 * Singleton class that manages debug callback hooks for the block rendering system.
 * Uses TrackedMap for reactivity, so components accessing these values will re-render
 * when callbacks are set or cleared.
 */
class DebugHooks {
  /**
   * Tracked callback registry for debug hooks.
   * Using TrackedMap enables reactivity when callbacks are set/cleared.
   *
   * @type {Map<string, Function|null>}
   */
  #callbacks = trackedMap(
    Object.values(DEBUG_CALLBACK).map((key) => [key, null])
  );

  /**
   * Gets a debug callback from the registry.
   *
   * @param {string} key - The callback key (use DEBUG_CALLBACK constants).
   * @returns {Function|null} The callback function, or null if not set.
   */
  getCallback(key) {
    return this.#callbacks.get(key);
  }

  /**
   * Sets a debug callback in the registry.
   * Used by dev-tools to register debug hooks.
   *
   * @param {string} key - The callback key (use DEBUG_CALLBACK constants).
   * @param {Function|null} value - The callback function, or null to clear.
   * @throws {Error} If the key is not a valid callback key.
   */
  setCallback(key, value) {
    if (!this.#callbacks.has(key)) {
      const validKeys = Object.values(DEBUG_CALLBACK).join(", ");
      throw new Error(
        `[Blocks] Unknown debug callback key: "${key}". Valid keys are: ${validKeys}.`
      );
    }
    this.#callbacks.set(key, value);
  }

  /**
   * Returns whether console logging is enabled.
   * Convenience getter that invokes the blockLogging callback.
   *
   * @returns {boolean} True if logging is enabled.
   */
  get isBlockLoggingEnabled() {
    return this.#callbacks.get(DEBUG_CALLBACK.BLOCK_LOGGING)?.() ?? false;
  }

  /**
   * Returns the outlet info component if outlet boundaries are enabled.
   * Invokes the OUTLET_INFO_COMPONENT callback which returns the component
   * when enabled, or null when disabled.
   *
   * @returns {typeof import("@glimmer/component").default|null} The outlet info component, or null.
   */
  get outletInfoComponent() {
    return this.#callbacks.get(DEBUG_CALLBACK.OUTLET_INFO_COMPONENT)?.();
  }

  /**
   * Returns whether outlet boundaries should be shown.
   * Derived from whether the outlet info component is available.
   *
   * @returns {boolean} True if boundaries should be shown.
   */
  get isOutletBoundaryEnabled() {
    return !!this.outletInfoComponent;
  }

  /**
   * Returns whether visual overlay is enabled.
   *
   * @returns {boolean} True if visual overlay is enabled.
   */
  get isVisualOverlayEnabled() {
    return this.#callbacks.get(DEBUG_CALLBACK.VISUAL_OVERLAY)?.() ?? false;
  }

  /**
   * Returns whether ghost blocks are enabled.
   *
   * @returns {boolean} True if ghost blocks are enabled.
   */
  get isGhostBlocksEnabled() {
    return this.#callbacks.get(DEBUG_CALLBACK.GHOST_BLOCKS)?.() ?? false;
  }

  /**
   * Returns the logger interface for conditions to use.
   * Convenience getter that invokes the loggerInterface callback.
   *
   * The interface has methods: logCondition, updateCombinatorResult,
   * updateConditionResult, logParamGroup, logRouteState.
   *
   * @returns {Object|null} The logger interface, or null if not available.
   */
  get loggerInterface() {
    return this.#callbacks.get(DEBUG_CALLBACK.LOGGER_INTERFACE)?.() ?? null;
  }
}

/**
 * Singleton instance of DebugHooks.
 * Import this to access debug callbacks with tracked reactivity.
 */
export const debugHooks = new DebugHooks();

/**
 * Handles an optional missing block by logging and optionally creating a ghost.
 *
 * When a block reference ends with `?` but the block is not registered, this
 * function handles the logging and ghost component creation.
 *
 * @param {Object} options - Options for handling the missing block.
 * @param {string} options.blockName - The name of the missing block.
 * @param {Object} options.entry - The block entry.
 * @param {string} options.hierarchy - The hierarchy path for logging.
 * @param {boolean} options.isLoggingEnabled - Whether debug logging is enabled.
 * @param {boolean} options.showGhosts - Whether to show ghost components.
 * @param {string} options.key - Stable unique key for this block.
 * @returns {import("discourse/lib/blocks/-internals/entry-processing").ChildBlockResult|null}
 *   Ghost component data with key if showGhosts is true, null otherwise.
 */
export function handleOptionalMissingBlock({
  blockName,
  entry,
  hierarchy,
  isLoggingEnabled,
  showGhosts,
  key,
}) {
  // Log if debug logging is enabled
  if (isLoggingEnabled) {
    debugHooks.getCallback(DEBUG_CALLBACK.OPTIONAL_MISSING_LOG)?.(
      blockName,
      entry.id,
      hierarchy
    );
  }

  // Show ghost if ghost blocks are enabled
  if (showGhosts) {
    const ghostData = createDebugGhost(
      {
        name: blockName,
        id: entry.id,
        args: entry.args,
        conditions: entry.conditions,
        failureType: FAILURE_TYPE.OPTIONAL_MISSING,
      },
      { outletName: hierarchy }
    );
    return ghostData ? { ...ghostData, key } : null;
  }

  return null;
}

/**
 * Builds a container path for nested containers.
 *
 * Maintains a count map to ensure unique indices for containers of the same type.
 * For example, if there are two "group" containers without ids, they get paths like:
 * - `baseHierarchy/group[0]`
 * - `baseHierarchy/group[1]`
 *
 * If a block has an id, it replaces the index (since the id is unique):
 * - `baseHierarchy/group(#my-id)`
 *
 * @param {string} blockName - The block name.
 * @param {string|null} blockId - The block's unique id (if set).
 * @param {string} baseHierarchy - The base hierarchy path.
 * @param {Map<string, number>} containerCounts - Map tracking container counts.
 * @returns {string} The full container path.
 */
export function buildContainerPath(
  blockName,
  blockId,
  baseHierarchy,
  containerCounts
) {
  // Always increment the counter for consistent indexing of blocks without ids.
  const count = containerCounts.get(blockName) ?? 0;
  containerCounts.set(blockName, count + 1);

  // Use id if available (unique), otherwise fall back to index.
  const suffix = blockId ? `(#${blockId})` : `[${count}]`;
  return `${baseHierarchy}/${blockName}${suffix}`;
}

/**
 * Invokes the BLOCK_DEBUG callback to create a ghost component.
 *
 * This is the low-level helper that calls the debug callback with block data.
 * Used by both `createGhostBlock` (entry-processing time) and `asGhost`
 * (render time) to avoid duplicating the callback invocation logic.
 *
 * @param {Object} blockData - Data describing the block to ghost.
 * @param {string} blockData.name - The block name.
 * @param {string} [blockData.id] - The block's unique ID (if set).
 * @param {Object} [blockData.args] - Block arguments.
 * @param {Object} [blockData.containerArgs] - Container arguments.
 * @param {Array} [blockData.conditions] - Block conditions.
 * @param {string} [blockData.failureType] - Type of failure (from FAILURE_TYPE).
 * @param {string} [blockData.failureReason] - Custom failure reason message.
 * @param {Array} [blockData.children] - Ghost children for containers.
 * @param {Object} context - Context for the ghost.
 * @param {string} context.outletName - The outlet/hierarchy name for display.
 * @param {Object} [context.outletArgs] - Outlet arguments.
 * @returns {Object|null} Ghost data with Component property, or null if callback
 *   not set or didn't return a component.
 */
export function createDebugGhost(blockData, context) {
  const ghostData = debugHooks.getCallback(DEBUG_CALLBACK.BLOCK_DEBUG)?.(
    {
      ...blockData,
      Component: null,
      conditionsPassed: false,
    },
    context
  );

  return ghostData?.Component ? ghostData : null;
}

/**
 * Creates a ghost component for an invisible block.
 *
 * Ghost components are shown in debug mode to visualize blocks that failed
 * their conditions or have no visible children.
 *
 * @param {Object} options - Options for creating the ghost.
 * @param {string} options.blockName - The block name.
 * @param {Object} options.entry - The block entry.
 * @param {string} options.hierarchy - The hierarchy path for display.
 * @param {string|undefined} options.containerPath - Container path for child hierarchies.
 * @param {boolean} options.isContainer - Whether this block is a container.
 * @param {import("@ember/owner").default} options.owner - The application owner.
 * @param {Object} options.outletArgs - Outlet arguments.
 * @param {boolean} options.isLoggingEnabled - Whether debug logging is enabled.
 * @param {Function} options.resolveBlockFn - Function to resolve block references.
 * @param {string} options.key - Stable unique key for this block.
 * @returns {import("discourse/lib/blocks/-internals/entry-processing").ChildBlockResult|null}
 *   Ghost component data with key if successful, null otherwise.
 */
export function createGhostBlock({
  blockName,
  entry,
  hierarchy,
  containerPath,
  isContainer,
  owner,
  outletArgs,
  isLoggingEnabled,
  resolveBlockFn,
  key,
}) {
  // For container blocks with children that failed due to no visible children,
  // recursively create ghost children so they appear nested in the debug overlay.
  let ghostChildren = null;
  if (
    isContainer &&
    entry.children?.length &&
    entry.__failureType === FAILURE_TYPE.NO_VISIBLE_CHILDREN
  ) {
    ghostChildren = debugHooks.getCallback(
      DEBUG_CALLBACK.GHOST_CHILDREN_CREATOR
    )?.(
      entry.children,
      owner,
      containerPath,
      outletArgs,
      isLoggingEnabled,
      resolveBlockFn
    );
  }

  const ghostData = createDebugGhost(
    {
      name: blockName,
      id: entry.id,
      args: entry.args,
      containerArgs: entry.containerArgs,
      conditions: entry.conditions,
      failureType: entry.__failureType,
      failureReason: entry.__failureReason,
      children: ghostChildren,
    },
    { outletName: hierarchy }
  );

  return ghostData ? { ...ghostData, key } : null;
}

/**
 * Executes a function within a debug console group.
 * Ensures START_GROUP and END_GROUP callbacks are always paired.
 *
 * @param {string} blockName - The block name for the group label.
 * @param {string|null} blockId - The block's unique ID (if set).
 * @param {string} hierarchy - The hierarchy path for context.
 * @param {boolean} isLoggingEnabled - Whether debug logging is active.
 * @param {() => boolean} fn - Function to execute that returns the condition result.
 * @returns {boolean} The result of the function execution.
 */
export function withDebugGroup(
  blockName,
  blockId,
  hierarchy,
  isLoggingEnabled,
  fn
) {
  if (!isLoggingEnabled) {
    return fn();
  }

  debugHooks.getCallback(DEBUG_CALLBACK.START_GROUP)?.(
    blockName,
    blockId,
    hierarchy
  );
  const result = fn();
  debugHooks.getCallback(DEBUG_CALLBACK.END_GROUP)?.(result);
  return result;
}
